/**
 * FollowUpAlertPoller — global popup-reminder for call-log follow-ups.
 *
 * Polls /api/call-logs/due every 60 s; when a follow-up assigned to the
 * current user (or their department) is due within 5 min — OR is overdue —
 * pops a top-right toast stack. Each toast has Snooze 15 min, Complete,
 * and Open call buttons.
 *
 * Snooze is local-only (in-memory Set keyed by call_log.id with a TTL) so
 * the same alert doesn't spam the user every minute. The Complete button
 * PATCHes /api/call-logs/{id} with status=completed and the alert dismisses
 * itself.
 *
 * Mounted in MainLayout once per session — no need to render it per route.
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { BellRing, X, CheckCircle2, Clock, ExternalLink } from 'lucide-react';

const POLL_MS = 60_000;          // hit /due once per minute
const SNOOZE_MS = 15 * 60_000;   // 15-min snooze when user clicks Snooze
const CHIME_FREQ = 880;          // Hz — short ping when a new alert appears

function playChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.frequency.value = CHIME_FREQ; o.type = 'sine';
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    o.connect(g); g.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + 0.4);
  } catch { /* audio is best-effort — silent failure is fine */ }
}

export default function FollowUpAlertPoller() {
  const [alerts, setAlerts] = useState([]);
  const snoozed = useRef(new Map()); // id → epoch_ms when snooze expires
  const lastSeen = useRef(new Set()); // ids we've already rendered (avoid re-chime)
  const navigate = useNavigate();

  const fetchDue = useCallback(async () => {
    try {
      const r = await api.get('/api/call-logs/due', { params: { lookahead_minutes: 5 } });
      const now = Date.now();
      // Drop any snoozes that have expired
      for (const [id, until] of snoozed.current.entries()) {
        if (until <= now) snoozed.current.delete(id);
      }
      const list = (r.data || []).filter(c => !snoozed.current.has(c.id));
      // Chime once per *new* alert id surfacing this poll
      const newOnes = list.filter(c => !lastSeen.current.has(c.id));
      if (newOnes.length) playChime();
      lastSeen.current = new Set(list.map(c => c.id));
      setAlerts(list);
    } catch (e) {
      // Endpoint may be unauthenticated at boot (e.g. login page) — silent fail
    }
  }, []);

  useEffect(() => {
    fetchDue();
    const t = setInterval(fetchDue, POLL_MS);
    return () => clearInterval(t);
  }, [fetchDue]);

  const dismiss = (id) => setAlerts(a => a.filter(x => x.id !== id));
  const snooze = (id) => {
    snoozed.current.set(id, Date.now() + SNOOZE_MS);
    dismiss(id);
  };
  const complete = async (id) => {
    try { await api.patch(`/api/call-logs/${id}`, { status: 'completed' }); } catch {}
    dismiss(id);
  };
  const openCall = (c) => {
    if (c.related_order_id) navigate(`/orders/${c.related_order_id}`);
    else navigate('/call-logs');
    dismiss(c.id);
  };

  if (!alerts.length) return null;

  return (
    <div style={{
      position: 'fixed', top: 70, right: 16, zIndex: 9000,
      display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 360,
    }}>
      {alerts.map(c => {
        const overdue = !!c.overdue;
        const minsLeft = Math.round((c.seconds_until_due || 0) / 60);
        return (
          <div key={c.id}
            style={{
              background: overdue ? '#fef2f2' : '#fffbeb',
              border: `1px solid ${overdue ? '#fca5a5' : '#fcd34d'}`,
              borderLeft: `4px solid ${overdue ? '#dc2626' : '#f59e0b'}`,
              borderRadius: 10, padding: 12,
              boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
              color: '#111827', fontSize: 13,
            }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <BellRing size={18} color={overdue ? '#dc2626' : '#f59e0b'} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: overdue ? '#991b1b' : '#92400e' }}>
                  {overdue ? 'Follow-up Overdue' : `Follow-up in ${minsLeft} min`}
                  {c.priority === 'STAT' && <span style={{ marginLeft: 6, padding: '1px 6px', border: '1px solid #dc2626', color: '#dc2626', borderRadius: 4, fontSize: 10 }}>STAT</span>}
                </div>
                <div style={{ marginTop: 2, fontSize: 12, color: '#374151' }}>
                  {c.call_type_label || c.call_type} · {c.spoke_with || '—'}
                </div>
                {c.reason && <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280',
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  overflow: 'hidden' }}>{c.reason}</div>}
                <div style={{ marginTop: 6, fontSize: 11, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Clock size={11} /> Due {new Date(c.follow_up_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hourCycle: 'h23' })}
                </div>
              </div>
              <button onClick={() => dismiss(c.id)} aria-label="Close"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 2 }}>
                <X size={16} />
              </button>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              <button onClick={() => snooze(c.id)}
                style={btn('transparent', '#6b7280', '#d1d5db')}>Snooze 15m</button>
              <button onClick={() => complete(c.id)}
                style={btn('#10b981', '#fff', '#10b981')}>
                <CheckCircle2 size={12} /> Complete
              </button>
              <button onClick={() => openCall(c)}
                style={btn('#6366f1', '#fff', '#6366f1')}>
                <ExternalLink size={12} /> Open
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const btn = (bg, color, border) => ({
  flex: 1, padding: '6px 8px', borderRadius: 6, border: `1px solid ${border}`,
  background: bg, color, cursor: 'pointer', fontSize: 11, fontWeight: 600,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
});
